/**
 * The component gallery, routed at `/_kit`.
 *
 * It exists for two audiences. A human uses it to see the whole kit in light and
 * dark without a backend, a session or a seeded database — the route is
 * deliberately **unguarded** for exactly that reason, and it reads no API. A page
 * author uses it as the catalogue: every element the kit ships, in every variant
 * and every state that has one, with the property that produced it named next to
 * it. If a state is not on this page it is not a state the kit supports.
 *
 * The underscore in `/_kit` marks it as a development surface rather than
 * product navigation, which is why it appears in no nav item.
 */

import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";

// The whole kit in one line — which is the barrel's reason for existing, and
// this page is its first consumer.
import "../components/index.js";
// Registers <sluice-theme-toggle>, the control this page puts at the top so the
// two themes can be compared without leaving it.
import "../theme.js";

import {
  decisionVariant,
  enforcementModeVariant,
  publishClassVariant,
  toast,
  toastPersistent,
  type BnBadgeVariant,
  type BnCodeChangeDetail,
  type BnCodeDiagnostic,
  type BnCodeDiagnosticClickDetail,
  type BnColumn,
  type BnKvCopyDetail,
  type BnKvRow,
  type BnPageChangeDetail,
  type BnRow,
  type BnRowClickDetail,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTab,
  type BnTabsChangeDetail,
} from "../components/index.js";

/* ── Fixtures ─────────────────────────────────────────────────────────────
   Everything below is invented data shaped like the real thing, so the gallery
   renders identically whether or not a control plane is running. */

const BADGES: BnBadgeVariant[] = [
  "neutral",
  "info",
  "success",
  "warning",
  "error",
  "enforce",
  "audit",
  "dry-run",
  "disabled",
  "allow",
  "deny",
  "reload",
  "restart",
  "not-enforced",
];

const CLAIM_OPS: BnSelectOption[] = [
  { value: "eq", label: "eq", description: "Exact match against the claim's string value." },
  { value: "ne", label: "ne", description: "Matches when the claim is present and different." },
  { value: "in", label: "in", description: "Matches when the claim is one of the listed values." },
  {
    value: "equals",
    label: "equals",
    description: "Not a Sluice operator.",
    disabled: true,
    disabledReason: "Sluice does not validate this field — 'equals' compiles and never matches.",
  },
];

const ERROR_CODES: BnSelectOption[] = [
  { value: "SL-1001", label: "SL-1001", description: "Statement rejected by an SqlAccessPolicy." },
  { value: "SL-1002", label: "SL-1002", description: "Table not reachable by this subject." },
  { value: "SL-1003", label: "SL-1003", description: "Column masked and referenced in a predicate." },
  { value: "SL-1004", label: "SL-1004", description: "Row filter produced a non-boolean result." },
  { value: "SL-2001", label: "SL-2001", description: "Datasource unreachable." },
  { value: "SL-2002", label: "SL-2002", description: "Datasource credentials rejected." },
  { value: "SL-3001", label: "SL-3001", description: "Rate limit exceeded for this subject." },
];

const SIZE_CLASSES: BnSelectOption[] = [
  { value: "s", label: "Small", description: "0.5 vCPU · 512 MiB. Development and staging." },
  { value: "m", label: "Medium", description: "1 vCPU · 1 GiB. The default for production." },
  { value: "l", label: "Large", description: "2 vCPU · 2 GiB." },
];

const TABLE_COLUMNS: BnColumn[] = [
  { key: "at", label: "At", width: "1px", mono: true },
  { key: "subject", label: "Subject" },
  { key: "decision", label: "Decision", width: "1px" },
  { key: "statement", label: "Statement", mono: true },
  { key: "ms", label: "ms", align: "right", width: "1px", mono: true },
];

/** Two "pages" of rows so the keyset pager has somewhere to go. */
const TABLE_PAGES: BnRow[][] = [
  [
    {
      id: "rec1",
      at: "12:04:19",
      subject: "alice@acme.example",
      decision: html`<bn-badge .variant=${decisionVariant("allow")}>allow</bn-badge>`,
      statement: "select id, total from orders limit 50",
      ms: "8",
    },
    {
      id: "rec2",
      at: "12:04:22",
      subject: "reporting-bot",
      decision: html`<bn-badge .variant=${decisionVariant("deny")}>deny</bn-badge>`,
      statement: "select * from payroll",
      ms: "2",
    },
    {
      id: "rec3",
      at: "12:04:31",
      subject: "bob@acme.example",
      decision: html`<bn-badge .variant=${decisionVariant("error")}>error</bn-badge>`,
      statement: "select * from orders join ledger using (id)",
      ms: "141",
    },
  ],
  [
    {
      id: "rec4",
      at: "12:05:02",
      subject: "alice@acme.example",
      decision: html`<bn-badge .variant=${decisionVariant("reject")}>reject</bn-badge>`,
      statement: "drop table orders",
      ms: "1",
    },
    {
      id: "rec5",
      at: "12:05:44",
      subject: "reporting-bot",
      decision: html`<bn-badge .variant=${decisionVariant("allow")}>allow</bn-badge>`,
      statement: "select count(*) from orders",
      ms: "17",
    },
  ],
];

const KV_ROWS: BnKvRow[] = [
  { label: "Record id", value: "k3x9q1m7p2vb8dn" },
  { label: "Hash", value: "9f2b0c4d7a1e5836b4c0d9e2f7a13b8c5d6e0f9a2b3c4d5e6f70819a2b3c4d5e" },
  { label: "Prior hash", value: "1a2b3c4d5e6f70819a2b3c4d5e6f70819a2b3c4d5e6f70819a2b3c4d5e6f7081" },
  { label: "Object key", value: "org/acme/inst/k3x9/2026/07/24/12.jsonl.zst" },
  { label: "Admin token", redacted: true },
  { label: "Rewritten SQL" },
];

const CONSOLE_TABS: BnTab[] = [
  { id: "overview", label: "Overview" },
  { id: "policies", label: "Policies" },
  { id: "audit", label: "Audit" },
  { id: "logs", label: "Logs", disabled: true },
];

const YAML_SAMPLE = `kind: SqlAccessPolicy
metadata:
  name: analysts-read-only
spec:
  enforcementMode: Audit
  subjects:
    - role: analyst
  tables:
    - public.orders
  conditions:
    - expression: request.ip.startsWith("10.")
`;

/** Offsets are computed from the fixture rather than written down, so editing
 *  the sample cannot silently move a finding onto the wrong span. */
function span(needle: string): { from: number; to: number } {
  const from = YAML_SAMPLE.indexOf(needle);
  return { from, to: from + needle.length };
}

const YAML_DIAGNOSTICS: BnCodeDiagnostic[] = [
  {
    ...span("public.orders"),
    severity: "error",
    ruleId: "L01",
    message: "A dotted table name never matches — Sluice compares a single segment.",
  },
  {
    ...span('request.ip.startsWith("10.")'),
    severity: "blocking-warning",
    ruleId: "L03",
    message: "request.* is not populated on the live query path (SLUICE-U12).",
  },
  {
    ...span("Audit"),
    severity: "info",
    ruleId: "L17",
    message: "Audit and DryRun are behaviourally identical in this build.",
  },
];

const CEL_SAMPLE = `subject.claims.department in ["finance", "ops"] && !has(subject.claims.contractor)`;

const SQL_SAMPLE = `select o.id, o.total, c.name
from orders o
join customers c on c.id = o.customer_id
where o.created_at > now() - interval '30 days'
order by o.total desc
limit 100;`;

/**
 * page-kit is the design-system gallery. It takes no properties: everything it
 * shows is a fixture defined in this module, so the page is renderable with no
 * network at all.
 *
 *   <page-kit></page-kit>
 */
@customElement("page-kit")
export class PageKit extends LitElement {
  /* Interactive state — one field per control that has to actually work, so the
     gallery exercises the events and not only the initial render. */
  @state() private checkedOn = true;
  @state() private checkedOff = false;
  @state() private text = "acme-production";
  @state() private claimOp = "eq";
  @state() private sizeClass = "m";
  @state() private codes: string[] = ["SL-1001"];
  @state() private tab = "overview";
  @state() private tablePage = 0;
  @state() private tableLoading = false;
  @state() private modalOpen = false;
  @state() private destructiveOpen = false;
  @state() private drawerOpen = false;
  @state() private cel = CEL_SAMPLE;
  @state() private lastEvent = "—";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      flex: 1 1 24rem;
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 60ch;
    }
    .theme {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .bus {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      overflow-wrap: anywhere;
    }

    section {
      margin-bottom: var(--bino-space-xl);
    }
    h2 {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      padding-bottom: var(--bino-space-xs);
      border-bottom: 1px solid var(--bino-border);
    }
    h3 {
      margin: 0 0 var(--bino-space-sm);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--bino-text-secondary);
    }

    /* One specimen: a monospace caption naming the property under test, and the
       component itself. The caption is what turns a gallery into a catalogue. */
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
      gap: var(--bino-space-lg);
    }
    .stack > * + * {
      margin-top: var(--bino-space-md);
    }
    .cap {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .swatches {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: center;
    }
    /* bn-code's shell is height:100%, so the *host* has to be given a height —
       otherwise 100% resolves against auto and the editor collapses. */
    .tall,
    .short {
      display: block;
    }
    .tall {
      height: 16rem;
    }
    .short {
      height: 9rem;
    }
    .tall > bn-code,
    .short > bn-code {
      display: block;
      height: 100%;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
  `;

  /* ── event sinks ─────────────────────────────────────────────────────
     Every specimen reports into one line, so a reader can confirm that a
     component actually emitted what its TSDoc claims. */

  private note(what: string) {
    this.lastEvent = what;
  }

  private onPageChange(e: CustomEvent<BnPageChangeDetail>) {
    // Fake the fetch the real page would do, including its latency, so the
    // table's busy state is visible rather than theoretical.
    const next = e.detail.direction === "next" ? this.tablePage + 1 : this.tablePage - 1;
    this.note(`page-change · ${e.detail.direction} · cursor=${e.detail.cursor ?? "null"}`);
    this.tableLoading = true;
    window.setTimeout(() => {
      this.tablePage = Math.max(0, Math.min(next, TABLE_PAGES.length - 1));
      this.tableLoading = false;
    }, 400);
  }

  /* ── sections ────────────────────────────────────────────────────────── */

  private renderButtons() {
    return html`
      <section>
        <h2>bn-button</h2>
        <h3>variant × size</h3>
        <div class="row">
          <bn-button variant="primary">Primary</bn-button>
          <bn-button variant="secondary">Secondary</bn-button>
          <bn-button variant="danger">Danger</bn-button>
          <bn-button variant="ghost">Ghost</bn-button>
        </div>
        <div class="row">
          <bn-button variant="primary" size="sm">Primary sm</bn-button>
          <bn-button variant="secondary" size="sm">Secondary sm</bn-button>
          <bn-button variant="danger" size="sm">Danger sm</bn-button>
          <bn-button variant="ghost" size="sm">Ghost sm</bn-button>
        </div>
        <h3>state</h3>
        <div class="row">
          <bn-button variant="primary" disabled>Disabled</bn-button>
          <bn-button variant="primary" loading>Publishing</bn-button>
          <bn-button variant="secondary" loading>Loading</bn-button>
          <bn-button variant="danger" disabled>Disabled danger</bn-button>
          <bn-button @click=${() => this.note("click · bn-button")}>Click me</bn-button>
        </div>
        <h3>block</h3>
        <bn-button variant="primary" block>Full width</bn-button>
      </section>
    `;
  }

  private renderBadges() {
    return html`
      <section>
        <h2>bn-badge</h2>
        <h3>every variant</h3>
        <div class="swatches">
          ${BADGES.map((v) => html`<bn-badge variant=${v}>${v}</bn-badge>`)}
        </div>
        <h3>dot · and the default label of not-enforced</h3>
        <div class="swatches">
          <bn-badge variant="success" dot>running</bn-badge>
          <bn-badge variant="warning" dot>degraded</bn-badge>
          <bn-badge variant="error" dot>failed</bn-badge>
          <bn-badge variant="not-enforced"></bn-badge>
        </div>
        <h3>the three mappers</h3>
        <div class="swatches">
          ${["Enforce", "Audit", "DryRun", "Disabled"].map(
            (m) => html`<bn-badge .variant=${enforcementModeVariant(m)}>${m}</bn-badge>`,
          )}
          ${["allow", "deny", "reject", "error"].map(
            (d) => html`<bn-badge .variant=${decisionVariant(d)}>${d}</bn-badge>`,
          )}
          ${["hot-reload", "rolling-restart"].map(
            (c) => html`<bn-badge .variant=${publishClassVariant(c)}>${c}</bn-badge>`,
          )}
        </div>
      </section>
    `;
  }

  private renderSurfaces() {
    return html`
      <section>
        <h2>bn-card · bn-panel · bn-empty-state · bn-spinner</h2>
        <div class="grid">
          <div>
            <p class="cap">heading + actions</p>
            <bn-card heading="Datasources">
              <bn-button slot="actions" variant="primary" size="sm">Add</bn-button>
              <p style="margin:0">Two datasources are configured for this instance.</p>
            </bn-card>
          </div>
          <div>
            <p class="cap">no heading</p>
            <bn-card><p style="margin:0">A bare surface with body padding.</p></bn-card>
          </div>
          <div>
            <p class="cap">flush (full-bleed body)</p>
            <bn-card heading="Recent queries" flush>
              <bn-table
                .columns=${[
                  { key: "subject", label: "Subject" },
                  { key: "ms", label: "ms", align: "right", mono: true },
                ] as BnColumn[]}
                .rows=${[
                  { subject: "alice@acme.example", ms: "8" },
                  { subject: "reporting-bot", ms: "17" },
                ]}
                caption="Recent queries"
              ></bn-table>
            </bn-card>
          </div>
          <div>
            <p class="cap">eyebrow</p>
            <bn-panel eyebrow="Blast radius">
              This is a restart-class publish. The instance is unavailable for roughly 10–25
              seconds and in-flight queries are cut short.
            </bn-panel>
          </div>
          <div>
            <p class="cap">no eyebrow</p>
            <bn-panel>One replica per instance is an invariant, not a default.</bn-panel>
          </div>
          <div>
            <p class="cap">size · label · block</p>
            <bn-card>
              <div class="row">
                <bn-spinner></bn-spinner>
                <bn-spinner size="28"></bn-spinner>
                <bn-spinner label="Provisioning"></bn-spinner>
              </div>
              <bn-spinner block size="28" label="Loading instances"></bn-spinner>
            </bn-card>
          </div>
        </div>
        <p class="cap" style="margin-top:var(--bino-space-lg)">bn-empty-state with an action</p>
        <bn-card flush>
          <bn-empty-state
            icon="◲"
            heading="No instances yet"
            message="Create one to start serving governed queries. Provisioning takes about a minute."
          >
            <bn-button slot="actions" variant="primary">New instance</bn-button>
          </bn-empty-state>
        </bn-card>
      </section>
    `;
  }

  private renderForms() {
    return html`
      <section>
        <h2>bn-field · bn-input · bn-checkbox</h2>
        <div class="grid">
          <div>
            <bn-field label="Instance name" required hint="Lowercase, 63 characters max.">
              <bn-input
                .value=${this.text}
                @input=${(e: CustomEvent<{ value: string }>) => (this.text = e.detail.value)}
              ></bn-input>
            </bn-field>
            <bn-field label="Digest" hint="Monospace values are read character by character.">
              <bn-input mono .value=${"sha256:9f2b0c4d7a1e5836"}></bn-input>
            </bn-field>
            <bn-field label="Disabled">
              <bn-input disabled placeholder="Not editable in this build"></bn-input>
            </bn-field>
          </div>
          <div>
            <bn-field label="Invalid, message on the field" error="An instance with that name already exists.">
              <bn-input invalid .value=${"acme-production"}></bn-input>
            </bn-field>
            <bn-field
              label="Blocking warning, with a rule chip"
              rule="L04"
              severity="blocking-warning"
              error="Never matches in this build — source IP is not populated on the live path."
            >
              <bn-input .value=${"10.0.0.0/8"}></bn-input>
            </bn-field>
            <bn-field label="Rich diagnostic, slotted" rule="L09" severity="warning">
              <bn-input .value=${"analysts"}></bn-input>
              <span slot="error"
                >Two bindings claim this subject —
                <a href="/_kit">the later one wins</a>.</span
              >
            </bn-field>
          </div>
          <div>
            <p class="cap">bn-input's own diagnostic (aria-describedby, inside its root)</p>
            <bn-input
              aria-label="Table name"
              mono
              .value=${"public.orders"}
              rule="L01"
              severity="error"
              diagnostic="A dotted name never matches — use one segment."
            ></bn-input>
            <p class="cap" style="margin-top:var(--bino-space-md)">warning severity</p>
            <bn-input
              aria-label="Row filter"
              mono
              .value=${"row.tenant == subject.id"}
              rule="L11"
              severity="warning"
              diagnostic="Compares an id to a tenant key; confirm the column type."
            ></bn-input>
          </div>
          <div>
            <p class="cap">bn-checkbox</p>
            <div class="stack">
              <bn-checkbox
                label="Allow restart"
                hint="This publish restarts the task; in-flight queries are dropped."
                .checked=${this.checkedOn}
                @change=${(e: CustomEvent<{ checked: boolean }>) =>
                  (this.checkedOn = e.detail.checked)}
              ></bn-checkbox>
              <bn-checkbox
                label="Send me the audit digest"
                .checked=${this.checkedOff}
                @change=${(e: CustomEvent<{ checked: boolean }>) =>
                  (this.checkedOff = e.detail.checked)}
              ></bn-checkbox>
              <bn-checkbox
                label="Enable query logs"
                hint="The pinned build does not emit them."
                disabled
              ></bn-checkbox>
              <bn-checkbox label="Disabled and checked" disabled checked></bn-checkbox>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderSelects() {
    return html`
      <section>
        <h2>bn-select</h2>
        <div class="grid">
          <div>
            <bn-field
              label="Claim operator"
              hint="Sluice does not validate this — a wrong op compiles and never matches."
            >
              <bn-select
                .options=${CLAIM_OPS}
                .value=${this.claimOp}
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                  this.claimOp = e.detail.value;
                  this.note(`change · bn-select · ${e.detail.value}`);
                }}
              ></bn-select>
            </bn-field>
          </div>
          <div>
            <bn-field label="Size class">
              <bn-select
                .options=${SIZE_CLASSES}
                .value=${this.sizeClass}
                @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                  (this.sizeClass = e.detail.value)}
              ></bn-select>
            </bn-field>
          </div>
          <div>
            <bn-field label="Error codes" hint="filterable + multiple">
              <bn-select
                filterable
                multiple
                .options=${ERROR_CODES}
                .values=${this.codes}
                placeholder="Any code"
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.codes = e.detail.values)}
              ></bn-select>
            </bn-field>
          </div>
          <div>
            <bn-field label="Invalid">
              <bn-select invalid .options=${SIZE_CLASSES} placeholder="Pick a size"></bn-select>
            </bn-field>
          </div>
          <div>
            <bn-field label="Disabled">
              <bn-select disabled .options=${SIZE_CLASSES} placeholder="Not available"></bn-select>
            </bn-field>
          </div>
          <div>
            <bn-field label="Empty catalogue">
              <bn-select
                filterable
                .options=${[] as BnSelectOption[]}
                .emptyText=${"No policies of this kind yet."}
                placeholder="Select…"
              ></bn-select>
            </bn-field>
          </div>
        </div>
      </section>
    `;
  }

  private renderTabs() {
    return html`
      <section>
        <h2>bn-tabs</h2>
        <p class="cap">roving tabindex · Left/Right/Home/End · one disabled tab</p>
        <bn-card>
          <bn-tabs
            label="Instance console"
            .tabs=${CONSOLE_TABS}
            .active=${this.tab}
            @change=${(e: CustomEvent<BnTabsChangeDetail>) => {
              this.tab = e.detail.id;
              this.note(`change · bn-tabs · ${e.detail.id}`);
            }}
          >
            <div slot="overview">Status, endpoint, size class and the live generation.</div>
            <div slot="policies">Eleven policy kinds, all authored as forms.</div>
            <div slot="audit">Every query the instance decided, hash-chained.</div>
            <div slot="logs">Not reachable — the tab is disabled.</div>
          </bn-tabs>
        </bn-card>
      </section>
    `;
  }

  private renderTables() {
    return html`
      <section>
        <h2>bn-table</h2>
        <p class="cap">clickable rows · keyset pager · mono columns · lit templates in cells</p>
        <bn-card flush>
          <bn-table
            caption="Audit records"
            clickable
            paginated
            .columns=${TABLE_COLUMNS}
            .rows=${TABLE_PAGES[this.tablePage] ?? []}
            .nextCursor=${this.tablePage < TABLE_PAGES.length - 1 ? "cursor-2" : null}
            ?loading=${this.tableLoading}
            @row-click=${(e: CustomEvent<BnRowClickDetail>) => {
              this.note(`row-click · ${String(e.detail.id)}`);
              this.drawerOpen = true;
            }}
            @page-change=${this.onPageChange}
          ></bn-table>
        </bn-card>

        <p class="cap" style="margin-top:var(--bino-space-lg)">loading (skeleton)</p>
        <bn-card flush>
          <bn-table loading skeleton-rows="3" .columns=${TABLE_COLUMNS} caption="Loading"></bn-table>
        </bn-card>

        <p class="cap" style="margin-top:var(--bino-space-lg)">empty · plain text</p>
        <bn-card flush>
          <bn-table
            .columns=${TABLE_COLUMNS}
            empty="No records match these filters."
            caption="Empty"
          ></bn-table>
        </bn-card>

        <p class="cap" style="margin-top:var(--bino-space-lg)">empty · slotted bn-empty-state</p>
        <bn-card flush>
          <bn-table .columns=${TABLE_COLUMNS} caption="Empty with slot">
            <bn-empty-state
              slot="empty"
              icon="∅"
              heading="Nothing ingested yet"
              message="Audit records appear here within a minute of the first query."
            >
              <bn-button slot="actions" size="sm">Open the playground</bn-button>
            </bn-empty-state>
          </bn-table>
        </bn-card>
      </section>
    `;
  }

  private renderKv() {
    return html`
      <section>
        <h2>bn-kv</h2>
        <p class="cap">value · redacted · unset · copy button per row</p>
        <bn-card>
          <bn-kv
            .rows=${KV_ROWS}
            @bn-copy=${(e: CustomEvent<BnKvCopyDetail>) => {
              this.note(`bn-copy · ${e.detail.label}`);
              toast(`${e.detail.label} copied`, "success");
            }}
          ></bn-kv>
        </bn-card>
      </section>
    `;
  }

  private renderOverlays() {
    return html`
      <section>
        <h2>bn-modal · bn-drawer · bn-toaster</h2>
        <p class="cap">
          focus is trapped while open, Escape closes, focus returns to the button that opened it
        </p>
        <div class="row">
          <bn-button @click=${() => (this.modalOpen = true)}>Open modal</bn-button>
          <bn-button variant="danger" @click=${() => (this.destructiveOpen = true)}>
            Open destructive modal
          </bn-button>
          <bn-button @click=${() => (this.drawerOpen = true)}>Open drawer</bn-button>
        </div>
        <div class="row">
          <bn-button size="sm" @click=${() => toast("Draft saved")}>toast · info</bn-button>
          <bn-button size="sm" @click=${() => toast("Generation 12 is live.", "success")}>
            toast · success
          </bn-button>
          <bn-button size="sm" @click=${() => toast("Ingest is 4 minutes behind.", "warning")}>
            toast · warning
          </bn-button>
          <bn-button size="sm" @click=${() => toast("The instance refused the publish.", "error")}>
            toast · error
          </bn-button>
          <bn-button
            size="sm"
            @click=${() =>
              toastPersistent(
                "Generation 12 is live on acme-production.",
                "success",
                "Publish finished",
              )}
          >
            toastPersistent
          </bn-button>
        </div>

        <bn-modal
          ?open=${this.modalOpen}
          heading="Rename instance"
          @close=${() => (this.modalOpen = false)}
        >
          <bn-field label="New name" hint="Renaming does not change the endpoint.">
            <bn-input .value=${"acme-production"}></bn-input>
          </bn-field>
          <bn-button slot="footer" @click=${() => (this.modalOpen = false)}>Cancel</bn-button>
          <bn-button slot="footer" variant="primary" @click=${() => (this.modalOpen = false)}>
            Rename
          </bn-button>
        </bn-modal>

        <bn-modal
          ?open=${this.destructiveOpen}
          tone="destructive"
          heading="Publish generation 12?"
          .width=${560}
          @close=${() => (this.destructiveOpen = false)}
        >
          <p style="margin:0 0 var(--bino-space-sm)">
            Two policies and one datasource changed since generation 11.
          </p>
          <p slot="consequence" style="margin:0">
            This is a restart-class publish. The instance is unavailable for roughly 10–25 seconds
            and in-flight queries are cut short.
          </p>
          <bn-button slot="footer" @click=${() => (this.destructiveOpen = false)}>Cancel</bn-button>
          <bn-button
            slot="footer"
            variant="danger"
            @click=${() => {
              this.destructiveOpen = false;
              toastPersistent("Generation 12 is live.", "success", "Publish finished");
            }}
          >
            Publish and restart
          </bn-button>
        </bn-modal>

        <bn-drawer
          ?open=${this.drawerOpen}
          heading="Audit record"
          .width=${560}
          @close=${() => (this.drawerOpen = false)}
        >
          <bn-kv .rows=${KV_ROWS}></bn-kv>
          <bn-button slot="footer" @click=${() => (this.drawerOpen = false)}>Close</bn-button>
          <bn-button slot="footer" variant="primary">Explain this decision</bn-button>
        </bn-drawer>
      </section>
    `;
  }

  private renderCode() {
    return html`
      <section>
        <h2>bn-code</h2>
        <p class="cap">
          CodeMirror arrives by dynamic import; until it does (and permanently if it fails) the
          text renders in a &lt;pre&gt; with the findings listed below it
        </p>
        <div class="stack">
          <div>
            <p class="cap">language="yaml" · read-only · three diagnostics · toolbar</p>
            <div class="tall">
              <bn-code
                language="yaml"
                label="Rendered SqlAccessPolicy"
                filename="policies.d/analysts-read-only.yaml"
                .value=${YAML_SAMPLE}
                .diagnostics=${YAML_DIAGNOSTICS}
                @bn-code-diagnostic-click=${(e: CustomEvent<BnCodeDiagnosticClickDetail>) =>
                  this.note(`bn-code-diagnostic-click · ${e.detail.ruleId ?? "(no rule)"}`)}
              ></bn-code>
            </div>
          </div>
          <div>
            <p class="cap">language="cel" · editable · hide-toolbar · hide-line-numbers</p>
            <div class="short">
              <bn-code
                language="cel"
                hide-toolbar
                hide-line-numbers
                .readonly=${false}
                .value=${this.cel}
                @bn-code-change=${(e: CustomEvent<BnCodeChangeDetail>) =>
                  (this.cel = e.detail.value)}
              ></bn-code>
            </div>
          </div>
          <div>
            <p class="cap">language="sql" · editable</p>
            <div class="short">
              <bn-code language="sql" .readonly=${false} .value=${SQL_SAMPLE}></bn-code>
            </div>
          </div>
          <div>
            <p class="cap">language="plain" · read-only</p>
            <div class="short">
              <bn-code
                language="plain"
                label="Provisioning log"
                .value=${"create service ok\nwait for converge …\nhealthy after 21s"}
              ></bn-code>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  override render() {
    return html`
      <header>
        <h1>Component kit</h1>
        <p class="lede">
          Every <code>bn-*</code> element the kit ships, in every variant and state. Flip the theme
          control to check both themes: no component branches on the theme — they consume
          <code>--bino-*</code> tokens, which pierce every shadow root.
        </p>
        <div class="theme">
          <span>Theme</span>
          <sluice-theme-toggle></sluice-theme-toggle>
        </div>
      </header>

      <bn-panel eyebrow="Last event">
        <span class="bus">${this.lastEvent}</span>
      </bn-panel>

      ${this.renderButtons()} ${this.renderBadges()} ${this.renderSurfaces()} ${this.renderForms()}
      ${this.renderSelects()} ${this.renderTabs()} ${this.renderTables()} ${this.renderKv()}
      ${this.renderOverlays()} ${this.renderCode()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-kit": PageKit;
  }
}
